/**
 * ReportsPreview — M6 Output Factory · Increment 1 (2026-06-25, Anil).
 *
 * A NO-LOGIN, READ-ONLY preview of the Report Composer SHELL (phase/version
 * structure + tier gating) for owner review on the preview channel — the
 * Composer is a staff surface behind RequireAuth, so this harness mounts it with
 * MOCK project data across L1 / L2 / L3 so the owner can SEE the phase/version
 * model and tier behaviour without logging in.
 *
 * Hostname-GATED exactly like /preview-intake (lib/previewHost.ts): reachable on
 * dev + Firebase preview channels ONLY; on the LIVE domains it renders NotFound
 * ("no previews on the live domain"). Nothing here writes Firebase — the shell's
 * `reportsOverride` + `onGenerate` seams drive it with mock data.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReportComposerShell from '../components/ReportComposerShell';
import EvaluationWorkspace from '../components/EvaluationWorkspace';
import ReportComposerCanvas from '../components/ReportComposerCanvas';
import type { ReportDoc } from '../lib/reportDelivery';
import {
  blankFinding,
  checklistTypesForTier,
  FINDING_IS_REAL,
  type Finding,
  type FindingType,
} from '../lib/findingsModel';
import { previewRouteAllowed } from '../lib/previewHost';
import NotFound from './NotFound';

/** Rich mock intake answers (the structured AD-040/041/042 data) so the readable
 * report has real content to render in the preview. */
function mockAnswers() {
  return {
    client_name: 'Rohit & Anita Sharma',
    property_address: 'Plot 14, Shanti Nagar, Indore',
    designProgramme: {
      spaces: [
        { space: 'Living room', qty: '1', targetArea: '320', floor: 'Ground Floor', priority: 'Must-have' },
        { space: 'Master bedroom', qty: '1', targetArea: '240', floor: 'First Floor', priority: 'Must-have' },
        { space: 'Pooja room', qty: '1', targetArea: '40', floor: 'Ground Floor', priority: 'Must-have' },
        { space: 'Kitchen', qty: '1', targetArea: '120', floor: 'Ground Floor', priority: 'Must-have' },
      ],
      householdType: 'Joint family', wfh: 'Yes — 1 person', hosting: 'Occasional',
      futureProofing: ['Growing family (more kids)', 'Ageing parents / accessibility', 'Pets'],
      archStyle: 'Modern minimal', interiorLeaning: 'Light / neutral',
      scopeOfWork: 'New build', budgetRange: '₹50 L – 1 Cr', phasing: 'Single phase', desiredStart: 'Within 3 months',
      constraints: ['Existing structure to retain', 'Trees to preserve'], mustKeeps: 'Keep the 30-yr-old mango tree in the SE',
      coordination: [
        { role: 'Architect', name: 'Ar. Meera Joshi', contactId: 'C00231', email: 'meera@studio.com', whatsapp: '+91 98xxx' },
        { role: 'Structural consultant', name: 'Rajesh Patel', contactId: 'C00188', whatsapp: '+91 99xxx' },
      ],
    },
    constructionDetails: {
      drawingStage: 'Developed design', structuralSystem: 'RCC framed', floors: 'G + 2 + terrace', builtUpSqft: '3200',
      prepared: ['Architectural', 'Structural'], waterSource: 'Municipal', ugTank: 'Yes', ohTank: 'Yes', waterNotes: 'UG 10,000 L + OH 2,000 L',
      elecSource: 'Grid + solar', backupPower: 'Inverter', solarProvision: 'Roof-ready',
      hvacApproach: 'Split AC (per room)', hvacAreas: 'all bedrooms + living',
      falseCeiling: 'All floors', lightingApproach: 'Layered (general + task + accent)',
      specLevel: 'Premium', plumbingGrade: 'Premium', electricalGrade: 'Premium', materialNotes: 'Jaquar fittings, vitrified flooring',
      windowSystem: 'uPVC', doorTypes: 'Flush', soilType: 'Black cotton', geotech: 'No', waterTable: 'No concern',
      constructionMethod: 'Conventional RCC', statutoryApproval: 'Applied', siteCondition: 'Vacant & ready', targetStart: 'Within 3 months',
    },
  };
}

const PREVIEW_TIERS = ['L1A', 'L1B', 'L1C', 'L2A', 'L2B', 'L2C', 'L2D', 'L3A', 'L3B'] as const;

/** Mock report docs (the FLAT {tier}_v{n} shape the engine writes). Existing docs
 * carry NO phase fields → they fold into the implicit "Phase 1". For L2/L3 we add
 * a couple of phase-tagged docs to show multi-phase + version history. */
function mockReports(tier: string): ReportDoc[] {
  const lvl = /^L([123])/.exec(tier)?.[1];
  if (lvl === '1') {
    // L1 = a single locked auto report (one phase, a couple of versions).
    return [
      { id: `${tier}_v1`, tier, version_n: 1, project_id: 'VP712', status: 'draft', generated_at: '2026-06-20T10:00:00Z' },
      { id: `${tier}_v2`, tier, version_n: 2, project_id: 'VP712', status: 'delivered', generated_at: '2026-06-22T10:00:00Z' },
    ];
  }
  // L2/L3 = phased delivery. Phase 1 (single-line plan + assessment) v1/v2/v3,
  // Phase 2 (final plan) v1. We tag phase fields on the docs to show grouping.
  return [
    { id: `${tier}_v1`, tier, version_n: 1, project_id: 'VP712', status: 'delivered', generated_at: '2026-06-10T10:00:00Z', phase_no: 1, phase_id: 'phase_1', phase_label: 'Single-line plan + assessment' } as ReportDoc,
    { id: `${tier}_v2`, tier, version_n: 2, project_id: 'VP712', status: 'delivered', generated_at: '2026-06-14T10:00:00Z', phase_no: 1, phase_id: 'phase_1', phase_label: 'Single-line plan + assessment' } as ReportDoc,
    { id: `${tier}_v3`, tier, version_n: 3, project_id: 'VP712', status: 'approved', generated_at: '2026-06-18T10:00:00Z', phase_no: 1, phase_id: 'phase_1', phase_label: 'Single-line plan + assessment' } as ReportDoc,
    { id: `${tier}_v4`, tier, version_n: 4, project_id: 'VP712', status: 'draft', generated_at: '2026-06-24T10:00:00Z', phase_no: 2, phase_id: 'phase_2', phase_label: 'Final plan' } as ReportDoc,
  ];
}

export default function ReportsPreview() {
  // GO-LIVE SAFETY: dev + preview-channel hosts only; NotFound on live.
  if (!previewRouteAllowed()) return <NotFound />;
  return <ReportsPreviewInner />;
}

/** Mock findings for a tier — the checklist types this tier shows, with a few
 * pre-confirmed (the real evaluation panels) + the rest to-assess / ai-drafted. */
function mockFindings(tier: string): Finding[] {
  return checklistTypesForTier(tier).map((type) => {
    const f = blankFinding('VP712', type);
    // map/grid/zone (the existing panels) come pre-confirmed; astro/health/verdict
    // start ai-drafted or to-assess so the confirm-loop is visible.
    if (FINDING_IS_REAL[type]) {
      return { ...f, source: 'auto', status: 'confirmed', confirmedBy: 'E001', confirmedAt: '2026-06-24T10:00:00Z' };
    }
    return { ...f, source: type === 'astro_remedies' ? 'composite' : 'manual', status: type === 'verdict' ? 'to_assess' : 'ai_drafted' };
  });
}

function ReportsPreviewInner() {
  const [params, setParams] = useSearchParams();
  const tier = (params.get('tier') || 'L3B').trim().toUpperCase();
  const reports = useMemo(() => mockReports(tier), [tier]);
  const [log, setLog] = useState<string[]>([]);
  function pushLog(m: string) { setLog((l) => [m, ...l].slice(0, 6)); }

  // Mock findings with INTERACTIVE confirm (local state — no Firebase).
  const [findings, setFindings] = useState<Finding[]>(() => mockFindings(tier));
  useEffect(() => { setFindings(mockFindings(tier)); }, [tier]);
  function confirmMock(type: FindingType) {
    setFindings((fs) => fs.map((f) => f.type === type ? { ...f, status: 'confirmed', confirmedBy: 'preview', confirmedAt: new Date().toISOString() } : f));
    pushLog(`(mock) Confirmed "${type}" → pool-ready.`);
  }

  // For the readable REPORT: take the (possibly just-confirmed) findings and
  // attach sample payloads to the astro/health/verdict ones, so the report has
  // real readable content. (In production the confirmed finding carries the
  // staff/engine payload.) A finding only reaches the report when confirmed —
  // the canvas honours that; here we also confirm them so the owner sees a full
  // report immediately.
  const PAYLOADS: Record<string, Record<string, unknown>> = {
    astro_vastu: { summary: 'Per-occupant Astro-Vastu favourability assessed for the four primary occupants.', readings: ['Rohit (self) — NW master bedroom favours career growth; SE study supports focus.', 'Anita (spouse) — SW placement strengthens stability; avoid NE bedroom.'] },
    astro_remedies: { summary: 'Astrology-based remedies dictated for this engagement.', remedies: ['Copper pyramid in the NE pooja zone', 'Silver in the SW for the elders'] },
    health_overlay: { summary: 'Per-person health considerations folded into the layout.', notes: ['Ageing parents — ground-floor bedroom + grab-bar-ready bathroom in the SE.', 'Kitchen ventilation prioritised (one occupant with respiratory sensitivity).'] },
    verdict: { summary: 'Overall a strong, buildable scheme. Two critical corrections in the NE and SW are addressed; with the remedies and the layout adjustments, the home is well-aligned for the joint family.' },
  };
  const reportFindings = useMemo<Finding[]>(
    () => findings.map((f) => PAYLOADS[f.type]
      ? { ...f, status: 'confirmed' as const, payload: { ...(f.payload || {}), ...PAYLOADS[f.type] } }
      : f),
    [findings],
  );
  const answers = useMemo(() => mockAnswers(), []);

  return (
    <div style={{ maxWidth: 920, margin: '0 auto', padding: '20px 16px 80px' }}>
      <div style={{ background: 'var(--warn-wash, #fbf3e1)', border: '1px solid var(--warn-line, #eccf93)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 12.5, color: 'var(--warn-deep, #9a6a12)' }}>
        <b>Preview mode (no login).</b> A faithful VIEW of the M6 Report Composer shell with MOCK project data — nothing saves; "Generate" is mocked here.
        On the real project page (staff, logged in) it wires the live report engine. Switch tiers below to compare L1 / L2 / L3 behaviour.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>Tier:</span>
        <select
          value={tier}
          onChange={(e) => setParams({ tier: e.target.value })}
          style={{ border: '1px solid var(--rule-strong, #cdd4da)', borderRadius: 8, padding: '7px 10px', fontSize: 13 }}
        >
          {PREVIEW_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <span style={{ fontSize: 11.5, color: 'var(--ink-mute)' }}>Sample project VP712 · client C00501</span>
      </div>

      <h2 style={{ fontSize: 18, margin: '0 0 10px' }}>Evaluation Workspace — {tier}</h2>
      <EvaluationWorkspace
        clientId="C00501"
        projectId="VP712"
        tierCode={tier}
        role="staff"
        findingsOverride={findings}
        onConfirm={(type) => confirmMock(type)}
        onMsg={(m) => pushLog(m)}
      />

      <h2 style={{ fontSize: 18, margin: '22px 0 10px' }}>Report Composer — {tier}</h2>
      <ReportComposerShell
        clientId="C00501"
        projectId="VP712"
        tierCode={tier}
        role="staff"
        reportsOverride={reports}
        phaseDefsOverride={[]}
        onGenerate={(phase) => pushLog(`(mock) Generate ${phase ? `into "${phase.label}"` : 'a new version'} — wires composeReport on the real page.`)}
        onMsg={(m) => pushLog(m)}
      />

      {/* M6 Inc-3 — the REAL, readable composed report (pick/drag + rendered). */}
      <h2 style={{ fontSize: 18, margin: '22px 0 10px' }}>The report — {tier}</h2>
      <ReportComposerCanvas
        tierCode={tier}
        answers={answers}
        findings={reportFindings}
        onMsg={(m) => pushLog(m)}
      />

      {log.length > 0 && (
        <div style={{ marginTop: 14, fontSize: 12, color: 'var(--ink-mute)' }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Activity (mock):</div>
          {log.map((l, i) => <div key={i}>· {l}</div>)}
        </div>
      )}
    </div>
  );
}
